import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {clearSelection} from 'src/components/data-table-selection';
import {GroupSuggest} from 'src/components/group-suggest';
import {SectionFilter} from 'src/components/section-filter';
import {formatPlatformValue} from 'src/components/user-platform-column/platform-columns';
import {selectAccessRights} from 'src/hooks/access-rights/services/access-rights-slice';
import {useUserStatuses, useUserTypes} from 'src/hooks/dictionaries';
import {getGroupName} from 'src/openapi/admin/apis/GetGroupNameApi';
import {UserStatus} from 'src/openapi/admin/models/UserStatus';
import {UserType} from 'src/openapi/admin/models/UserType';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import Button, {FlatButton} from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import {Pill, StripOfPills} from '@pb/kit-component-strip-of-pills';

import {SELECTION_KEY} from '../../../users-groups-search-result/constants';
import {userSearchResultStateActions, userSearchResultStateSelector} from '../../../users-groups-search-result/slices/user-search-result-state.slice';

import {applyUsersFilters, getUsersFilters, platformAccessKey} from './criteria';
import {useAccountPlatformAccess} from './use-account-platform-access';
import {useAccountProductOptions} from './use-account-product-options';

import type {UsersFilters} from './criteria';
import type {UserPlatformAccess} from 'src/openapi/admin/models/UserPlatformAccess';
import type {UserSearchCriteria} from 'src/openapi/admin/models/UserSearchCriteria';

type Props = {accountId: number, refreshVersion: number, children: React.ReactNode, actions: React.ReactNode};

const platformAccessTypeOrder: Record<UserType, number> = {
    [UserType.T_LC]: 0,
    [UserType.T_MB]: 1,
    [UserType.T_CRM]: 2,
    [UserType.T_CA]: 3,
    [UserType.T_NA]: 4,
    [UserType.T_UNSUPPORTED]: 5,
};
const platformAccessStatusOrder: Record<UserStatus, number> = {
    [UserStatus.ENABLED]: 0,
    [UserStatus.SUSPENDED]: 1,
    [UserStatus.DISABLED]: 2,
    [UserStatus.EMPTY]: 3,
};

export const UsersFilter = ({accountId, refreshVersion, children, actions}: Props) => {
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();
    const {searchCriteria} = useSelector(userSearchResultStateSelector);
    const {groupId, platformAccess, productExternalKeys} = searchCriteria as UserSearchCriteria;
    const rights = useSelector(selectAccessRights);
    const productsAllowed = !!rights?.includes('USER_PRODUCTS_VIEW');
    const filters = useMemo(() => getUsersFilters({groupId, platformAccess,
        productExternalKeys: productsAllowed ? productExternalKeys : undefined,
    }), [groupId, platformAccess, productExternalKeys, productsAllowed]);
    const products = useAccountProductOptions(accountId, refreshVersion, productsAllowed);
    const productOptions = useMemo(() => {
        const byKey = new Map((products.data || []).map(product => [product.productExternalKey, product]));
        for (const key of productExternalKeys || []) {
            if (!byKey.has(key)) {
                byKey.set(key, {productExternalKey: key, name: products.names[key] || key});
            }
        }
        const options = [...byKey.values()];
        return options.map(product => ({...product,
            label: options.some(other => (
                other.productExternalKey !== product.productExternalKey && other.name === product.name
            ))
                ? `${product.name} (${product.productExternalKey})` : product.name,
        })).sort((a, b) => a.label.localeCompare(b.label) || a.productExternalKey.localeCompare(b.productExternalKey));
    }, [products.data, products.names, productExternalKeys]);
    const available = useAccountPlatformAccess(accountId, refreshVersion);
    const {getUserTypeByCode} = useUserTypes();
    const {getUserStatusByCode} = useUserStatuses();
    const [groupLabel, setGroupLabel] = useState<{id: number, name: string}>();

    useEffect(() => {
        if (!groupId) {
            return;
        }
        const controller = new AbortController();
        const load = async () => {
            try {
                const response = await apiDispatch(getGroupName({requestBody: [Number(groupId)]}, {
                    signal: controller.signal,
                }));
                if (!controller.signal.aborted && response.payload.success) {
                    setGroupLabel({id: Number(groupId), name: response.payload.data[groupId] || String(groupId)});
                }
            } catch {
                // Keep the group ID visible and removable if its name is unavailable.
            }
        };
        load();
        return () => controller.abort();
    }, [accountId, groupId, apiDispatch]);

    const label = useCallback(({type, status}: UserPlatformAccess) => formatPlatformValue(
        getUserTypeByCode(type)?.description || type,
        getUserStatusByCode(status)?.description || status,
    ), [getUserTypeByCode, getUserStatusByCode]);

    const options = useMemo(() => [...new Map([
        ...available.data || [], ...platformAccess || [],
    ].map(pair => [platformAccessKey(pair), pair])).values()].sort((a, b) => (
        platformAccessTypeOrder[a.type] - platformAccessTypeOrder[b.type]
        || platformAccessStatusOrder[a.status] - platformAccessStatusOrder[b.status]
    )), [available.data, platformAccess]);

    const apply = useCallback((nextFilters: UsersFilters) => {
        const next = applyUsersFilters(searchCriteria as UserSearchCriteria, {
            ...nextFilters, productExternalKeys: productsAllowed ? nextFilters.productExternalKeys : undefined,
        });
        if (!isDeepEqual({prev: searchCriteria, current: next})) {
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(userSearchResultStateActions.setSearchCriteria(next));
        }
    }, [searchCriteria, dispatch, productsAllowed]);

    const reset = useCallback(() => {
        apply({});
        return {};
    }, [apply]);
    const active = !!filters.groupId || !!filters.platformAccess?.length || !!filters.productExternalKeys?.length;

    return (
        <Group direction="v" gap="m">
            <Group justify="space-between">
                <Group>
                    {children}
                    <SectionFilter<UsersFilters>
                        filters={filters}
                        defaultFilters={{}}
                        onApply={apply}
                        onClear={reset}
                        width={465}
                        maxHeight={500}
                        getAnchor={({anchorRef, positionRef, getAnchorProps}) => (
                            <Button
                                variant="Type.SECONDARY"
                                startIcon={<Icon type={active ? Icons.FILTER_FILLED : Icons.FILTER_EMPTY}/>}
                                endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}
                                ref={anchorRef}
                                {...getAnchorProps()}>
                                <b>Filter</b>
                            </Button>
                        )}>
                        {(draft, setDraft) => (
                            <Group direction="v" gap="s">
                                <Label htmlFor="users-group-filter">Group</Label>
                                <GroupSuggest
                                    id="users-group-filter"
                                    accountId={accountId}
                                    selectedGroupId={draft.groupId || null}
                                    placeholder="Select target group"
                                    width="100%"
                                    onSelect={group => setDraft(previous => ({...previous, groupId: group?.id}))}
                                />
                                <Label htmlFor="platform-access-filter">Platform Access</Label>
                                {available.error ? (
                                    <Message color="error">
                                        <Group gap="s">
                                            <span>{available.error.message || 'Failed to load Platform Access'}</span>
                                            <FlatButton onClick={available.retry}>Retry</FlatButton>
                                        </Group>
                                    </Message>
                                ) : null}
                                <ComboBox
                                    id="platform-access-filter"
                                    aria-label="Platform Access"
                                    multiple={true}
                                    disabled={available.loading || !!available.error || !available.data?.length}
                                    placeholder={available.loading ? 'Loading Platform Access…' : 'All Platform Access'}
                                    value={(draft.platformAccess || []).map(platformAccessKey)}
                                    onChange={(keys: string[]) => setDraft(previous => ({
                                        ...previous,
                                        platformAccess: options.filter(pair => keys.includes(platformAccessKey(pair))),
                                    }))}>
                                    {options.map(pair => (
                                        <ComboboxItem
                                            key={platformAccessKey(pair)}
                                            value={platformAccessKey(pair)}
                                            caption={label(pair)}
                                        />
                                    ))}
                                </ComboBox>
                                {productsAllowed ? (
                                    <>
                                        <Label htmlFor="product-filter">Product</Label>
                                        {products.error ? (
                                            <Message color="error">
                                                <Group gap="s">
                                                    <span>{products.error.message || 'Failed to load products'}</span>
                                                    <FlatButton onClick={products.retry}>Retry</FlatButton>
                                                </Group>
                                            </Message>
                                        ) : null}
                                        <ComboBox
                                            id="product-filter"
                                            aria-label="Product"
                                            multiple={true}
                                            disabled={products.loading || !!products.error
                                                || !products.data || !productOptions.length}
                                            placeholder={products.loading ? 'Loading products…' : 'All Products'}
                                            value={draft.productExternalKeys || []}
                                            onChange={(keys: string[]) => setDraft(previous => ({
                                                ...previous, productExternalKeys: keys,
                                            }))}>
                                            {productOptions.map(product => (
                                                <ComboboxItem
                                                    key={product.productExternalKey}
                                                    value={product.productExternalKey}
                                                    caption={product.label}
                                                />
                                            ))}
                                        </ComboBox>
                                    </>
                                ) : null}
                            </Group>
                        )}
                    </SectionFilter>
                    {active ? (
                        <FlatButton startIcon={<Icon type={Icons.TRASH}/>} onClick={reset}>
                            Clear all criteria
                        </FlatButton>
                    ) : null}
                </Group>
                {actions}
            </Group>
            {active ? (
                <Group gap="m" style={{flexWrap: 'wrap'}}>
                    {filters.groupId ? (
                        <StripOfPills label="Group">
                            <Pill
                                name={groupLabel?.id === filters.groupId ? groupLabel.name : String(filters.groupId)}
                                removable={true}
                                preventedRemoveHandler={() => apply({...filters, groupId: undefined})}
                            />
                        </StripOfPills>
                    ) : null}
                    {filters.platformAccess?.length ? (
                        <StripOfPills label="Platform Access">
                            {filters.platformAccess.map(pair => (
                                <Pill
                                    key={platformAccessKey(pair)}
                                    name={label(pair)}
                                    removable={true}
                                    preventedRemoveHandler={() => apply({
                                        ...filters,
                                        platformAccess: filters.platformAccess?.filter(item => (
                                            platformAccessKey(item) !== platformAccessKey(pair)
                                        )),
                                    })}
                                />
                            ))}
                        </StripOfPills>
                    ) : null}
                    {filters.productExternalKeys?.length ? (
                        <StripOfPills label="Product">
                            {filters.productExternalKeys.map(key => (
                                <Pill
                                    key={key}
                                    name={productOptions.find(product => (
                                        product.productExternalKey === key
                                    ))?.label || key}
                                    removable={true}
                                    preventedRemoveHandler={() => apply({...filters,
                                        productExternalKeys: filters.productExternalKeys?.filter(value => (
                                            value !== key
                                        )),
                                    })}
                                />
                            ))}
                        </StripOfPills>
                    ) : null}
                </Group>
            ) : null}
        </Group>
    );
};
